import React, { useState, useContext, useCallback, useEffect } from 'react';
import { shape } from 'prop-types';
import Box from '@mui/material/Box';
import { TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import debounce from 'lodash.debounce';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import './GoogleAddressInput.scss';
import { DeliveryAddressDataContext } from '../../context-api/DeliveryAddressDataProvider';
// https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform

export default function GoogleAddressInput(props) {
  const { styleAutoComplete, error } = props;

  const {
    googleAddress,
    setGoogleAddress,
    googleAddressPredictions,
    setGoogleAddressPredictionsHandler,
  } = useContext(DeliveryAddressDataContext);

  const [options, setOptions] = useState([]);

  useEffect(() => {
    return () => {
      console.log('GoogleAddressInput will unmount');
      setGoogleAddress({});
    };
  }, []);

  const googleAutocomplete = async (inputTargetValue) => {
    return new Promise((resolve, reject) => {
      try {
        let sessionToken = new window.google.maps.places.AutocompleteSessionToken();

        const Data = {
          input: inputTargetValue,
          sessionToken,
          componentRestrictions: { country: 'us' },
        };

        const autocomplete =
          new window.google.maps.places.AutocompleteService().getPlacePredictions(Data, resolve);

        autocomplete.then((res) => {
          if (res.predictions && res.predictions.length) {
            const { predictions } = res;

            const options = predictions.map((obj) => ({
              ...obj,
              label: obj.description,
            }));

            setOptions(options);

            setGoogleAddressPredictionsHandler({
              key: inputTargetValue,
              predictions,
            });
          }
        });
      } catch (e) {
        console.log('Error occurred while searching address:', e);
        reject(e);
      }
    });
  };

  /**
   * Get ZIP Code using Place Details API
   */
  const getPlaceZipcode = (placeId) => {
    return new Promise((resolve) => {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      placesService.getDetails(
        {
          placeId,
          fields: ['address_components'],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place?.address_components
          ) {
            const postalCode =
              place.address_components.find((component) => component.types.includes('postal_code'))
                ?.long_name || '';

            resolve(postalCode);
          } else {
            resolve('');
          }
        }
      );
    });
  };

  const debounceData = useCallback(
    debounce((newInputValue) => {
      if (googleAddressPredictions[newInputValue]) {
        const options = googleAddressPredictions[newInputValue].predictions.map((obj) => ({
          ...obj,
          label: obj.description,
        }));

        setOptions(options);
      } else {
        googleAutocomplete(newInputValue);
      }
    }, 1000),
    [googleAddressPredictions]
  );

  const handleOnInputChange = (event, newInputValue) => {
    if (newInputValue.length >= 2) {
      debounceData(newInputValue);
    }
  };

  const onChangeAutoComplete = async (event, newValue) => {
    if (newValue !== null) {
      setOptions(newValue ? [newValue, ...options] : options);

      const [addressLine, city, state, country] = newValue.description.split(',');

      // Fetch ZIP Code from Google Place Details
      const zipcode = await getPlaceZipcode(newValue.place_id);

      const addressData = {
        ...newValue,
        addressLine: addressLine?.trim(),
        city: city?.trim(),
        state: state?.trim(),
        country: country?.trim(),
        zipcode,
      };

      setGoogleAddress(addressData);
    } else {
      setGoogleAddress({});
    }
  };

  return (
    <Autocomplete
      id='google-map-demo'
      size='small'
      sx={{
        width: 420,
        height: 'auto',
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
        ...styleAutoComplete,
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={googleAddress.description || ''}
      onChange={onChangeAutoComplete}
      onInputChange={handleOnInputChange}
      className='custom-css-outlined-input-googleaddress'
      renderOption={(props, option) => {
        const [addressLine, city, state, country] = option.description.split(',');

        return (
          <div
            {...props}
            style={{
              display: 'flex',
              lineHeight: '15px',
            }}
          >
            <Box>
              <LocationOnIcon
                fontSize='medium'
                style={{
                  position: 'relative',
                  display: 'block',
                }}
              />
            </Box>

            <span style={{ display: 'flex', flexWrap: 'wrap' }}>
              {`${addressLine}, `}
              <span style={{ fontSize: '11px', color: 'gray' }}>
                {`${city}, ${state}, ${country}`}
              </span>
            </span>
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label='Search address'
          fullWidth
          error={error}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              height: 40,
              alignItems: 'center',
            },
            '& .MuiInputLabel-root': {
              top: '50%',
              transform: 'translate(14px, -50%) scale(1)',
            },
            '& .MuiInputLabel-shrink': {
              top: 0,
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          }}
        />
      )}
    />
  );
}

GoogleAddressInput.propTypes = {
  styleAutoComplete: shape(),
};

GoogleAddressInput.defaultProps = {
  styleAutoComplete: {},
};
